/**
 * Archetype classifier — fires fire-and-forget at session Close.
 *
 * Calls Edge Function phase 13, parses the JSON archetype hits, and upserts
 * each hit into user_archetype_assignments for Awakening-tier users.
 *
 * Thresholds (per build plan):
 *   >= 0.70 → upsert to DB (stored, not yet surfaced)
 *   >= 0.85 → eligible to surface to user (checked in Journal/Profile UI)
 */
import { db, supabase } from './supabase'
import { callClaude } from './claude'

interface ArchetypeHit {
  archetype_id: string
  confidence: number
  evidence_quote: string
}

/** Valid archetype IDs — classifier must only return these. */
const VALID_ARCHETYPE_IDS = new Set([
  'rescuer', 'abandoned', 'controller', 'performer', 'invisible',
  'caretaker', 'rebel', 'perfectionist', 'orphan', 'warrior', 'pleaser', 'victim',
])

/**
 * Classify archetypes for a completed session and upsert results to DB.
 * Safe to call fire-and-forget — catches and swallows all errors.
 * Only runs for Awakening (premium) tier users.
 */
export async function classifyArchetypes(
  sessionId: string,
  storyText: string,
  patternReflection: string,
  choiceText: string,
  emotion: string,
  intensity: number
): Promise<void> {
  if (!supabase) return

  // Gate: Awakening tier only
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return

  const { data: profile } = await db()
    .from('profiles')
    .select('subscription_tier')
    .eq('id', user.id)
    .maybeSingle()
  const tier = (profile as { subscription_tier: string } | null)?.subscription_tier ?? 'free'
  if (tier !== 'premium') return

  // Build session context string for the classifier
  const sessionData = JSON.stringify({
    story: storyText,
    pattern: patternReflection,
    choice: choiceText,
  })

  const result = await callClaude({
    phase: 13,
    emotion,
    intensity,
    messages: [{ role: 'user', content: '[Classify archetypes]' }],
    sessionId,
    optionContext: sessionData,
  })

  if ('error' in result) return

  // Parse JSON — Claude sometimes wraps in markdown fences despite instructions
  let hits: ArchetypeHit[] = []
  try {
    const cleaned = result.response
      .trim()
      .replace(/^```json?\s*/i, '')
      .replace(/\s*```$/, '')
      .trim()
    const parsed = JSON.parse(cleaned)
    if (!Array.isArray(parsed)) return
    hits = parsed
  } catch {
    // Malformed JSON — swallow silently. Classifier is non-critical.
    return
  }

  for (const hit of hits) {
    // Validate shape and archetype ID whitelist
    if (
      typeof hit.archetype_id !== 'string' ||
      !VALID_ARCHETYPE_IDS.has(hit.archetype_id) ||
      typeof hit.confidence !== 'number' ||
      hit.confidence < 0.70
    ) continue

    // Check for permanent rejection — never re-insert a rejected archetype
    const { data: existing } = await db()
      .from('user_archetype_assignments')
      .select('id, observation_count, source_session_ids, intensity, user_rejected')
      .eq('user_id', user.id)
      .eq('archetype_id', hit.archetype_id)
      .maybeSingle()

    const ex = existing as {
      id: string
      observation_count: number
      source_session_ids: string[]
      intensity: number
      user_rejected: boolean
    } | null

    if (ex?.user_rejected) continue  // permanently suppressed by user

    if (ex) {
      // Update: increment observation count, update intensity if higher, add session
      await db()
        .from('user_archetype_assignments')
        .update({
          observation_count: ex.observation_count + 1,
          last_seen_at: new Date().toISOString(),
          intensity: Math.max(ex.intensity, hit.confidence),
          source_session_ids: [...new Set([...ex.source_session_ids, sessionId])],
          // Only update evidence_quote if this hit has higher confidence
          ...(hit.confidence > ex.intensity && hit.evidence_quote
            ? { evidence_quote: hit.evidence_quote }
            : {}),
        })
        .eq('id', ex.id)
    } else {
      // Insert new emerging assignment
      await db()
        .from('user_archetype_assignments')
        .insert({
          user_id: user.id,
          archetype_id: hit.archetype_id,
          state: 'emerging',
          intensity: hit.confidence,
          observation_count: 1,
          source_session_ids: [sessionId],
          evidence_quote: hit.evidence_quote ?? null,
        })
    }
  }
}
